import { useAuth } from "@workos-inc/authkit-react";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import UpdatesView from "@/shared/components/Updates/UpdatesView";
import { SidebarInset, SidebarProvider } from "@/shared/components/ui/sidebar";
import { getAdminPermissions } from "@/shared/config/adminPermissions";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import AdminSidebar, { type AdminView } from "./components/AdminSidebar";
import AdminCalendarView from "./views/AdminCalendarView";
import AdminDashboardView from "./views/AdminDashboardView";
import AdminSupportView from "./views/AdminSupportView";
import BillingView from "./views/BillingView";
import EntriesView from "./views/EntriesView";

/**
 * Admin Dashboard Page
 *
 * Main entry point for firm administrators.
 * Shows different views based on admin permissions.
 */
export default function AdminPage() {
	const { user } = useAuth();
	const { canView } = getAdminPermissions(user?.email);
	const location = useLocation();
	const navigate = useNavigate();

	// Derive current view from URL path
	const currentView: AdminView = useMemo(() => {
		const segments = location.pathname.split("/").filter(Boolean);
		const segment = segments[1] || "";
		switch (segment) {
			case "billing":
				return "billing";
			case "entries":
				return "entries";
			case "calendar":
				return "calendar";
			case "support":
				return "support";
			case "dashboard":
				return "dashboard";
			case "updates":
				return "updates";
			default:
				return "dashboard";
		}
	}, [location.pathname]);

	const adminTitles: Record<AdminView, string> = {
		dashboard: "Admin - Dashboard",
		billing: "Admin - Billing",
		entries: "Admin - Entries",
		calendar: "Admin - Calendar",
		support: "Admin - Support",
		faq: "Admin - FAQ",
		updates: "Admin - Updates",
	};
	useDocumentTitle(adminTitles[currentView]);

	// Determine first permitted view for redirect
	const getInitialView = (): AdminView => {
		if (canView.overview) return "dashboard";
		if (canView.billing) return "billing";
		if (canView.entries) return "entries";
		if (canView.calendar) return "calendar";
		if (canView.support) return "support";
		return "dashboard";
	};

	// Redirect /admin to first permitted view
	useEffect(() => {
		const path = location.pathname.replace(/\/+$/, "");
		if (path === "/admin") {
			navigate(`/admin/${getInitialView()}`, { replace: true });
		}
	}, [location.pathname, navigate]);
	const renderView = () => {
		switch (currentView) {
			case "dashboard":
				return <AdminDashboardView />;
			case "billing":
				return <BillingView />;
			case "entries":
				return <EntriesView />;
			case "calendar":
				return <AdminCalendarView />;
			case "support":
				return <AdminSupportView />;
			case "updates":
				return <UpdatesView />;
			default:
				return <AdminDashboardView />;
		}
	};

	return (
		<SidebarProvider>
			<AdminSidebar permissions={canView} />
			<SidebarInset className="bg-background overflow-auto">
				<ErrorBoundary>{renderView()}</ErrorBoundary>
			</SidebarInset>
		</SidebarProvider>
	);
}
